import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import ledger, { ledgerError } from "../../../api/ledger";
import BillCanvas from "./BillCanvas";

/* ═══════════════════════════════════════════════════════════════════════════
   BILL DESIGNER
   ───────────────────────────────────────────────────────────────────────────
   Which blocks the printed bill carries, and in what order — per store, per
   paper size.

   ── WHY THIS IS A LIST AND NOT A CANVAS ──
   A thermal bill is a single column of characters, 48 of them on 80mm paper
   and 32 on 58mm. There is nothing to drag: a block is either in the column or
   it is not, and the only spatial decision is what follows what. A canvas
   would invite a shopkeeper to place a logo "here" and then print it
   somewhere else, which is a worse lie than a plain list.

   ── THE PREVIEW IS THE PRINTER ──
   The pane on the right is not drawn by this page. It is rendered by the
   service, by the same code that composes the bytes the printer receives, and
   returned as characters. A designer that draws its own preview eventually
   disagrees with the printer, and the shop finds out on paper.

   ── WHAT CANNOT BE SWITCHED OFF ──
   The server marks some blocks locked — the shop name, the address, the GSTIN,
   the title, the bill number, the items, the totals. They are what make the
   document a tax invoice, and a bill without them earns a fine. Their switches
   are shown, disabled, rather than hidden: "you may not remove this" is a
   different statement from "this does not exist", and the first is the true one.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Blocks whose height the bill decides, and roughly how many lines they take
   on the canvas. The server keeps the same list; these numbers are only for
   giving each one room on screen. */
const TALL_ROWS = {
  items: 5, totals: 4, gstSummary: 3, tenders: 2, upiQr: 3, logo: 3, address: 2, customer: 1,
};

/* ── A LAYOUT THAT HAS NEVER BEEN ARRANGED ──
   Every layout saved before the canvas existed is a FLOW: an ordered list with
   no rows and no columns, because the old screen had no notion of either. Drawn
   on a grid as-is, all of it lands at row 0 — every field stacked on top of
   every other, which is exactly what it looked like.

   So a layout without positions is given them, once, on the way into the
   canvas: down the page in its existing order, each field a full line wide,
   tall fields given the lines they need. Nothing moves on the paper — a
   top-to-bottom arrangement prints exactly as the flow did — but from then on
   every field has a place, which is what makes it draggable at all. */
const placeFlow = (blocks, cols) => {
  if (blocks.some((b) => b.row !== undefined && b.row !== null)) return blocks;
  let row = 0;
  return blocks.map((b) => {
    const placed = { ...b, row, col: 0, width: cols };
    row += TALL_ROWS[b.key] || 1;
    return placed;
  });
};

const PAPERS = [
  { key: "80mm", label: "80 mm", hint: "48 columns — the counter printer" },
  { key: "58mm", label: "58 mm", hint: "32 columns — a portable printer" },
];

const SOURCE_TEXT = {
  store: { label: "This store's own layout", tone: "own" },
  estate: { label: "Inherited from the estate", tone: "inherited" },
  builtIn: { label: "The standard layout", tone: "inherited" },
};

const S = {
  page:      { maxWidth: 1180, margin: "0 auto", padding: "8px 0 96px", fontFamily: "var(--mly-font)", color: "var(--mly-text)" },
  head:      { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 18 },
  title:     { margin: 0, fontFamily: "var(--mly-heading, var(--mly-font))", fontSize: 24, fontWeight: 800, letterSpacing: -0.2 },
  sub:       { margin: "6px 0 0", color: "var(--mly-text-muted)", fontSize: 13, maxWidth: 620, lineHeight: 1.5 },
  bar:       { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 16 },
  select:    { fontFamily: "inherit", fontSize: 13, padding: "8px 12px", borderRadius: 10, border: "1px solid var(--mly-border)", background: "var(--mly-surface)", color: "var(--mly-text)", minWidth: 190 },
  segment:   { display: "inline-flex", border: "1px solid var(--mly-border)", borderRadius: 10, overflow: "hidden" },
  seg:       { fontFamily: "inherit", fontSize: 13, fontWeight: 600, padding: "8px 14px", border: 0, background: "var(--mly-surface)", color: "var(--mly-text-muted)", cursor: "pointer" },
  segOn:     { background: "var(--mly-accent)", color: "var(--mly-on-accent)" },
  chip:      { fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", padding: "4px 10px", borderRadius: 999 },
  chipOwn:   { background: "var(--mly-accent-soft)", color: "var(--mly-accent)" },
  chipInh:   { background: "var(--mly-surface-2)", color: "var(--mly-text-muted)" },
  grid:      { display: "grid", gridTemplateColumns: "minmax(190px, 230px) minmax(340px, 1fr) minmax(300px, 420px)", gap: 16, alignItems: "start" },
  card:      { background: "var(--mly-surface)", border: "1px solid var(--mly-border)", borderRadius: 14, boxShadow: "var(--mly-shadow-sm)", overflow: "hidden" },
  row:       { display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 14px", borderBottom: "1px solid var(--mly-border-soft, var(--mly-border))" },
  rowOff:    { opacity: 0.55 },
  grip:      { display: "flex", flexDirection: "column", gap: 2, paddingTop: 2 },
  move:      { width: 22, height: 18, borderRadius: 6, border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text-muted)", cursor: "pointer", fontSize: 10, lineHeight: 1, padding: 0 },
  label:     { fontSize: 14, fontWeight: 600 },
  hint:      { fontSize: 11, color: "var(--mly-text-muted)", marginTop: 2, lineHeight: 1.45 },
  text:      { marginTop: 8, width: "100%", fontFamily: "inherit", fontSize: 13, padding: "7px 10px", borderRadius: 8, border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text)" },
  locked:    { fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--mly-text-muted)", background: "var(--mly-surface-2)", borderRadius: 999, padding: "2px 8px", marginLeft: 8 },
  paper:     { position: "sticky", top: 16 },
  paperIn:   { background: "#fff", color: "#111", borderRadius: 10, padding: "18px 14px", fontFamily: "var(--mly-font-mono)", fontSize: 11.5, lineHeight: 1.45, whiteSpace: "pre", overflowX: "auto", minHeight: 260, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.08)" },
  paperTop:  { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid var(--mly-border)", fontSize: 12, color: "var(--mly-text-muted)" },
  actions:   { display: "flex", gap: 8, justifyContent: "flex-end", padding: "12px 14px", borderTop: "1px solid var(--mly-border)", position: "sticky", bottom: 0, background: "var(--mly-surface)" },
  btn:       { fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "9px 16px", borderRadius: 10, border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text)", cursor: "pointer" },
  btnMain:   { fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "9px 18px", borderRadius: 10, border: "1px solid var(--mly-accent)", background: "var(--mly-accent)", color: "var(--mly-on-accent)", cursor: "pointer" },
  off:       { opacity: 0.55, cursor: "not-allowed" },
  note:      { margin: "0 0 14px", padding: "10px 14px", borderRadius: 10, background: "var(--mly-warn-soft)", color: "var(--mly-text)", fontSize: 13, border: "1px solid var(--mly-border)" },
  palette:   { background: "var(--mly-surface)", border: "1px solid var(--mly-border)", borderRadius: 14, padding: 10, alignSelf: "start", position: "sticky", top: 16, maxHeight: "calc(100vh - 120px)", overflowY: "auto" },
  paletteHead:{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--mly-text-muted)", padding: "2px 4px" },
  paletteHint:{ fontSize: 11, color: "var(--mly-text-muted)", padding: "2px 4px 8px", lineHeight: 1.45 },
  paletteItem:{ display: "flex", alignItems: "center", gap: 6, width: "100%", textAlign: "left", fontFamily: "inherit",
                fontSize: 12.5, padding: "7px 9px", marginBottom: 4, borderRadius: 9, cursor: "grab",
                border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text)" },
  paletteOn: { opacity: 0.5, cursor: "not-allowed" },
  paletteFlag:{ marginLeft: "auto", fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--mly-text-muted)" },
  paletteMany:{ marginLeft: "auto", fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--mly-accent)" },
  emptyBill: { padding: "34px 16px", textAlign: "center", color: "var(--mly-text-muted)", fontSize: 13 },
  rowOn:     { background: "var(--mly-accent-soft)" },
  tag:       { fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--mly-accent)", background: "var(--mly-surface-2)", borderRadius: 999, padding: "1px 7px", marginLeft: 6 },
  remove:    { border: "none", background: "transparent", color: "var(--mly-text-muted)", cursor: "pointer", fontSize: 13, padding: "0 2px", alignSelf: "flex-start" },
  props:     { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", padding: "10px 12px", borderTop: "1px solid var(--mly-border)", background: "var(--mly-surface-2)" },
  propsTitle:{ fontSize: 12, fontWeight: 700, color: "var(--mly-heading)", marginRight: 4 },
  propGroup: { display: "inline-flex", gap: 4 },
  propText:  { fontFamily: "inherit", fontSize: 12, padding: "5px 9px", borderRadius: 8, minWidth: 180,
               border: "1px solid var(--mly-border)", background: "var(--mly-surface)", color: "var(--mly-text)" },
  prop:      { fontFamily: "inherit", fontSize: 11.5, fontWeight: 600, padding: "5px 9px", borderRadius: 8,
               border: "1px solid var(--mly-border)", background: "var(--mly-surface)", color: "var(--mly-text)", cursor: "pointer" },
  propOn:    { borderColor: "var(--mly-accent)", background: "var(--mly-accent-soft)", color: "var(--mly-accent)" },
  dragging:  { opacity: 0.4 },
  dropTarget:{ borderTop: "2px solid var(--mly-accent)" },
  dots:      { display: "block", textAlign: "center", fontSize: 13, color: "var(--mly-text-muted)", cursor: "grab", lineHeight: 1.1 },
  tplBar:    { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "10px 12px", marginTop: 14, borderTop: "1px solid var(--mly-border)" },
  tplTitle:  { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--mly-text-muted)" },
  tplNone:   { fontSize: 12, color: "var(--mly-text-muted)" },
  tpl:       { display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 999, border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)" },
  tplOn:     { borderColor: "var(--mly-accent)", background: "var(--mly-accent-soft)" },
  tplName:   { border: "none", background: "transparent", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: "var(--mly-text)", cursor: "pointer", padding: 0 },
  tplFlag:   { fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--mly-accent)" },
  tplAct:    { border: "none", background: "transparent", fontFamily: "inherit", fontSize: 11, color: "var(--mly-text-muted)", cursor: "pointer", padding: 0 },
  tplInput:  { fontFamily: "inherit", fontSize: 12.5, padding: "6px 10px", borderRadius: 9, width: 170, border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text)" },
  logoRow:   { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 14px", marginBottom: 12, background: "var(--mly-surface)", border: "1px solid var(--mly-border)", borderRadius: 12 },
  logoLabel: { fontSize: 13, fontWeight: 700, color: "var(--mly-heading)" },
  logoState: { flex: 1, fontSize: 12, color: "var(--mly-text-muted)", fontVariantNumeric: "tabular-nums" },
  logoBtn:   { fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 9, border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text)", cursor: "pointer" },
  logoOff:   { opacity: 0.5, cursor: "not-allowed" },
  err:       { margin: "0 0 14px", padding: "10px 14px", borderRadius: 10, background: "var(--mly-danger-soft)", color: "var(--mly-danger)", fontSize: 13 },
};

const Switch = ({ on, disabled, onChange, label }) => (
  <label
    title={label}
    style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto",
      width: 22, height: 22, borderRadius: 7, marginTop: 1,
      border: `1.5px solid ${on ? "var(--mly-accent)" : "var(--mly-border)"}`,
      background: on ? "var(--mly-accent)" : "var(--mly-surface)",
      color: "var(--mly-on-accent)", cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.55 : 1,
    }}
  >
    <input type="checkbox" checked={on} disabled={disabled} aria-label={label}
      onChange={(e) => onChange?.(e.target.checked)}
      style={{ position: "absolute", opacity: 0, width: 0, height: 0 }} />
    {on && (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.7 5.3a1.3 1.3 0 0 1 0 1.9L10.2 17.7a1.3 1.3 0 0 1-1.9 0l-5-5a1.3 1.3 0 0 1 1.9-1.9l4.1 4.1 9.5-9.6a1.3 1.3 0 0 1 1.9 0Z" /></svg>
    )}
  </label>
);

export default function BillDesigner() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [paper, setPaper] = useState("80mm");

  const [catalogue, setCatalogue] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [saved, setSaved] = useState([]);          // what the server holds, for the dirty check
  const [source, setSource] = useState("builtIn");

  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const spec = useCallback((key) => catalogue.find((c) => c.key === key) || {}, [catalogue]);

  /* ── the stores this account may design for ─────────────────────────── */
  useEffect(() => {
    ledger
      .get("/admin/stores")
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : r.data?.data || [];
        setStores(rows);
      })
      .catch(() => setStores([]));
  }, []);

  /* The shop's logo: its size in printer dots, or null when none is stored.
     A raster has no characters, so the preview shows it as [ logo WxH ] --
     the point is that the block does something. */
  const [logo, setLogo] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);

  /* The layouts this shop keeps, and the name being saved under. A shop that
     has only ever had one sees one row and can ignore all of it. */
  const [templates, setTemplates] = useState([]);
  const [saveAsName, setSaveAsName] = useState("");

  /* The field whose appearance is being edited. */
  const [selected, setSelected] = useState(null);

  /* A field dragged off the palette carries its key; the drop on the paper
     turns it into a placed field at that cell. Moving a field already on the
     bill is the canvas's own business -- it drags by the pointer, cell by
     cell, because that is the only way a position means anything. */
  const [dragNewKey, setDragNewKey] = useState(null);

  const clearDrag = () => setDragNewKey(null);



  /* ── the layout in force ────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await ledger.get("/admin/print/layout", { params: { storeId: storeId || undefined, paper } });
      setCatalogue(data.blocks || []);
      setLogo(data.logo || null);
      setTemplates(data.templates || []);
      const placed = placeFlow(data.layout?.blocks || [], data.layout?.paper === "58mm" ? 32 : 48);
      setBlocks(placed);
      setSaved(placed);
      setSource(data.source || "builtIn");
    } catch (e) {
      setError(ledgerError(e));
    } finally {
      setLoading(false);
    }
  }, [storeId, paper]);

  useEffect(() => { load(); }, [load]);

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true); setError("");
    try {
      const png = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(new Error("That file could not be read."));
        r.readAsDataURL(file);
      });
      await ledger.post("/admin/print/logo", { storeId: storeId || undefined, paper, png });
      load();
    } catch (e) { setError(ledgerError(e)); }
    finally { setLogoBusy(false); }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await ledger.delete("/admin/print/logo", { params: { storeId: storeId || undefined } });
      load();
    } catch (e) { setError(ledgerError(e)); }
    finally { setLogoBusy(false); }
  };

  const saveAs = async () => {
    const name = saveAsName.trim();
    if (!name) return;
    setSaving(true); setError("");
    try {
      await ledger.post("/admin/print/templates", {
        storeId: storeId || undefined, paper, layout: { paper, blocks }, name,
      });
      setSaveAsName("");
      load();
    } catch (e) { setError(ledgerError(e)); }
    finally { setSaving(false); }
  };

  const openTemplate = async (id) => {
    setError("");
    try {
      const { data } = await ledger.get(`/admin/print/templates/${id}`, { params: { storeId: storeId || undefined } });
      setBlocks(placeFlow(data.layout?.blocks || [], cols));
    } catch (e) { setError(ledgerError(e)); }
  };

  const makeDefault = async (id) => {
    setSaving(true); setError("");
    try {
      await ledger.post(`/admin/print/templates/${id}/default`, {}, { params: { storeId: storeId || undefined, paper } });
      load();
    } catch (e) { setError(ledgerError(e)); }
    finally { setSaving(false); }
  };

  const removeTemplate = async (id, name) => {
    if (!window.confirm(`Delete the layout "${name}"?`)) return;
    setSaving(true); setError("");
    try {
      await ledger.delete(`/admin/print/templates/${id}`, { params: { storeId: storeId || undefined } });
      load();
    } catch (e) { setError(ledgerError(e)); }
    finally { setSaving(false); }
  };

  /* ── the preview, from the printer's own code ───────────────────────── */
  const timer = useRef(null);
  useEffect(() => {
    if (blocks.length === 0) return undefined;
    clearTimeout(timer.current);
    // Debounced: every tick of a checkbox would otherwise be a request, and the
    // preview is a whole bill.
    timer.current = setTimeout(() => {
      ledger
        .post("/admin/print/layout/preview", { storeId: storeId || undefined, paper, layout: { paper, blocks } })
        .then((r) => setPreview(r.data?.text || ""))
        .catch((e) => setError(ledgerError(e)));
    }, 220);
    return () => clearTimeout(timer.current);
  }, [blocks, paper, storeId]);

  const dirty = useMemo(
    () => JSON.stringify(blocks) !== JSON.stringify(saved),
    [blocks, saved]
  );

  const setProp = (id, patch) =>
    setBlocks((prev) => prev.map((b) => ((b.id || b.key) === id ? { ...b, ...patch } : b)));


  const removeItem = (id) => {
    setBlocks((prev) => prev.filter((b) => (b.id || b.key) !== id));
    setSelected((cur) => (cur === id ? null : cur));
  };

  /* A field the shop can put on the bill more than once. Everything else is
     placed once: a second GSTIN is not a design choice. */
  const REPEATABLE = ["text", "separator", "spacer"];

  const makeItem = (key) => {
    const c = spec(key);
    if (!c.key) return null;
    const many = REPEATABLE.includes(key);
    if (!many && blocks.some((b) => b.key === key)) return null;
    return {
      key,
      id: many ? Math.random().toString(36).slice(2, 10) : key,
      on: true,
      text: c.hasText ? "" : undefined,
    };
  };

  /* Adding at a cell -- where the field was dropped -- or at the first free
     row when it was clicked rather than dragged. */
  const addItem = (key, at) => {
    const item = makeItem(key);
    if (!item) return;
    const placed = at
      ? { ...item, row: at.row, col: at.col, width: Math.max(6, cols - at.col) }
      : { ...item, row: nextFreeRow(), col: 0, width: cols };
    setBlocks((prev) => [...prev, placed]);
    setSelected(placed.id);
  };

  const cols = paper === "58mm" ? 32 : 48;

  const nextFreeRow = () => {
    let last = -1;
    blocks.forEach((b) => { if ((b.row ?? -1) > last) last = b.row ?? -1; });
    return last + 1;
  };

  const onBill = (key) => blocks.some((b) => b.key === key);
  const chosen = blocks.find((b) => (b.id || b.key) === selected) || null;
  const chosenSpec = chosen ? spec(chosen.key) : {};

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await ledger.put("/admin/print/layout", {
        storeId: storeId || undefined,
        paper,
        layout: { paper, blocks },
      });
      setBlocks(data.layout?.blocks || blocks);
      setSaved(data.layout?.blocks || blocks);
      setSource(data.source || source);
      toast.success(storeId ? "This store's bill layout is saved" : "The estate bill layout is saved");
    } catch (e) {
      setError(ledgerError(e));
      toast.error(ledgerError(e));
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    if (!window.confirm("Throw away this layout and go back to the inherited one?")) return;
    setSaving(true); setError("");
    try {
      const { data } = await ledger.delete("/admin/print/layout", { params: { storeId: storeId || undefined, paper } });
      const placed = placeFlow(data.layout?.blocks || [], data.layout?.paper === "58mm" ? 32 : 48);
      setBlocks(placed);
      setSaved(placed);
      setSource(data.source || "builtIn");
      toast.success(data.removed ? "Back to the inherited layout" : "There was nothing of its own to remove");
    } catch (e) {
      setError(ledgerError(e));
      toast.error(ledgerError(e));
    } finally {
      setSaving(false);
    }
  };

  const tone = SOURCE_TEXT[source] || SOURCE_TEXT.builtIn;

  return (
    <div style={S.page}>
      <div style={S.head}>
        <div>
          <h1 style={S.title}>Bill Designer</h1>
          <p style={S.sub}>
            Choose what the printed bill carries and in what order. The preview is
            rendered by the printer's own code, so what you see is what the paper
            says. The shop's name, address and GSTIN come from Store Settings.
          </p>
        </div>
      </div>

      <div style={S.bar}>
        <select style={S.select} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
          <option value="">All stores (estate default)</option>
          {stores.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <div style={S.segment}>
          {PAPERS.map((p) => (
            <button key={p.key} type="button" title={p.hint}
              style={{ ...S.seg, ...(paper === p.key ? S.segOn : null) }}
              onClick={() => setPaper(p.key)}>
              {p.label}
            </button>
          ))}
        </div>

        <span style={{ ...S.chip, ...(tone.tone === "own" ? S.chipOwn : S.chipInh) }}>{tone.label}</span>
      </div>

      {/* The logo lives with the layout because that is where somebody looks
          for it. A PNG, reduced to one bit per dot on the way in -- which is
          all a thermal printer can put on paper. */}
      <div style={S.logoRow}>
        <span style={S.logoLabel}>Shop logo</span>
        <span style={S.logoState}>
          {logo ? `${logo.widthDots} x ${logo.heightDots} dots` : "None — the logo block prints nothing"}
        </span>
        <label style={{ ...S.logoBtn, ...(logoBusy ? S.logoOff : null) }}>
          {logoBusy ? "Working…" : logo ? "Replace" : "Upload a PNG"}
          <input type="file" accept="image/png" style={{ display: "none" }} disabled={logoBusy}
            onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {logo && (
          <button type="button" style={S.logoBtn} disabled={logoBusy} onClick={removeLogo}>Remove</button>
        )}
      </div>

      {source === "estate" && storeId && (
        <div style={S.note}>
          This store prints the estate layout. Saving here gives it one of its own;
          every other store keeps the estate's.
        </div>
      )}
      {error && <div style={S.err}>{error}</div>}

      <div style={S.grid}>
        {/* ── the fields you can put on a bill ── */}
        <div style={S.palette}>
          <div style={S.paletteHead}>Fields</div>
          <div style={S.paletteHint}>Drag one onto the bill, or click to add it at the end.</div>
          {catalogue.map((c) => {
            const placed = !REPEATABLE.includes(c.key) && onBill(c.key);
            return (
              <button
                key={c.key}
                type="button"
                draggable={!placed}
                onDragStart={(e) => { setDragNewKey(c.key); e.dataTransfer.effectAllowed = "copy"; }}
                onDragEnd={clearDrag}
                disabled={placed}
                onClick={() => addItem(c.key)}
                title={c.hint || c.label}
                style={{ ...S.paletteItem, ...(placed ? S.paletteOn : null) }}
              >
                <span>{c.label || c.key}</span>
                {placed && <span style={S.paletteFlag}>on the bill</span>}
                {REPEATABLE.includes(c.key) && <span style={S.paletteMany}>many</span>}
              </button>
            );
          })}
        </div>

        {/* ── the paper ── */}
        <div>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "var(--mly-text-muted)" }}>Loading the layout…</div>
          ) : (
            <>
              <BillCanvas
                cols={cols}
                blocks={blocks}
                spec={spec}
                selected={selected}
                onSelect={setSelected}
                onChange={(id, patch) => setProp(id, patch)}
                onRemove={removeItem}
                onDropNew={(row, col) => { if (dragNewKey) { addItem(dragNewKey, { row, col }); clearDrag(); } }}
              />

                            {/* What the chosen field looks like. Only what was asked for is
                  applied -- a field with no choice keeps the printer's own
                  habit, which is right for a total or a table. */}
              {chosen && (
                <div style={S.props}>
                  <span style={S.propsTitle}>{chosenSpec.label || chosen.key}</span>
                  {chosenSpec.hasText && (
                    <input type="text" value={chosen.text || ""} style={S.propText}
                      onChange={(e) => setProp(selected, { text: e.target.value })}
                      placeholder={chosen.key === "separator" ? "solid, dotted, double, dash, star" : "Your own words"} />
                  )}
                  <Switch on={chosen.on !== false} disabled={!!chosenSpec.locked}
                    onChange={(v) => setProp(selected, { on: v })} label="Printed" />
                  <div style={S.propGroup}>
                    {[["", "default"], ["left", "left"], ["centre", "centre"], ["right", "right"]].map(([v, label]) => (
                      <button key={label} type="button"
                        style={{ ...S.prop, ...((chosen.align || "") === v ? S.propOn : null) }}
                        onClick={() => setProp(selected, { align: v || undefined })}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <button type="button" style={{ ...S.prop, ...(chosen.bold ? S.propOn : null) }}
                    onClick={() => setProp(selected, { bold: !chosen.bold })}>bold</button>
                  <button type="button" style={{ ...S.prop, ...(chosen.big ? S.propOn : null) }}
                    onClick={() => setProp(selected, { big: !chosen.big })}>big</button>
                  <span style={{ flex: 1 }} />
                  <button type="button" style={S.prop} onClick={() => setSelected(null)}>done</button>
                </div>
              )}

              {/* Saved layouts. The one marked "prints" is what a bill uses;
                  the others are kept for a festival, a second paper size, or
                  simply as the version that worked. */}
              <div style={S.tplBar}>
                <span style={S.tplTitle}>Saved layouts</span>
                {templates.length === 0 && <span style={S.tplNone}>None saved yet</span>}
                {templates.map((t) => (
                  <span key={t.id} style={{ ...S.tpl, ...(t.isDefault ? S.tplOn : null) }}>
                    <button type="button" style={S.tplName} title="Open this layout" onClick={() => openTemplate(t.id)}>
                      {t.name}
                    </button>
                    {t.isDefault
                      ? <span style={S.tplFlag}>prints</span>
                      : (
                        <>
                          <button type="button" style={S.tplAct} title="Print this one from now on"
                            onClick={() => makeDefault(t.id)}>make default</button>
                          <button type="button" style={S.tplAct} title="Delete this layout"
                            onClick={() => removeTemplate(t.id, t.name)}>✕</button>
                        </>
                      )}
                  </span>
                ))}
                <span style={{ flex: 1 }} />
                <input type="text" value={saveAsName} placeholder="Save this as…" style={S.tplInput}
                  onChange={(e) => setSaveAsName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveAs(); }} />
                <button type="button" style={{ ...S.btn, ...(!saveAsName.trim() || saving ? S.off : null) }}
                  disabled={!saveAsName.trim() || saving} onClick={saveAs}>Save as</button>
              </div>

              <div style={S.actions}>
                <button type="button" style={{ ...S.btn, ...(source !== "store" || saving ? S.off : null) }}
                  disabled={source !== "store" || saving} onClick={revert}>
                  Use the inherited layout
                </button>
                <button type="button" style={{ ...S.btn, ...(!dirty || saving ? S.off : null) }}
                  disabled={!dirty || saving} onClick={() => setBlocks(saved)}>
                  Undo changes
                </button>
                <button type="button" style={{ ...S.btnMain, ...(!dirty || saving ? S.off : null) }}
                  disabled={!dirty || saving} onClick={save}>
                  {saving ? "Saving…" : "Save layout"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── the paper ── */}
        <div style={S.paper}>
          <div style={S.card}>
            <div style={S.paperTop}>
              <span>Preview</span>
              <span>{paper === "58mm" ? "32 columns" : "48 columns"}</span>
            </div>
            <div style={{ padding: 12, background: "var(--mly-surface-2)" }}>
              <div style={S.paperIn}>{preview || "…"}</div>
            </div>
            <div style={{ padding: "10px 14px", fontSize: 11, color: "var(--mly-text-muted)", borderTop: "1px solid var(--mly-border)" }}>
              A sample basket, with a long product name, a weighed line, a discount
              and two tax rates — the cases a layout usually breaks on. The logo and
              the QR are shown as markers; on paper they are printed.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
